import './StoreConfirmSizes.scss';

import React, { useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  STORE_CONFIRM_SIZES_DESIGN_HEADER_SUBTITLE,
  STORE_CONFIRM_SIZES_NOTE,
} from '../constants/storeCheckout.constants';
import ProductSizeSelection from './ProductSizeSelection/ProductSizeSelection';
import StockWarningMessage from './StockWarningMessage/StockWarningMessage';
import { useStoreConfirmSizes } from './helpers/useStoreConfirmSizes';
import type { IStoreConfirmSizesProps, ISizesFormValues } from './helpers/storeConfirmSizes.types';
import { V3_APP_BASE_URL } from '@/constants/Config';
import Icon from '@/components/Icon/Icon';
import Textarea from '@/components/Textarea/Textarea';

interface IStoreConfirmSizesContainerProps extends IStoreConfirmSizesProps {
  sizesForm: UseFormReturn<ISizesFormValues>;
  onValidityChange?: (isValid: boolean) => void;
}

const StoreConfirmSizes: React.FC<IStoreConfirmSizesContainerProps> = ({
  designGroups,
  sizesForm,
  isReadOnly = false,
  isEditStorePage = false,
  sizeOrderCounts = {},
  isSalesUser = false,
  storeId = null,
  onValidityChange,
}) => {
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const {
    productsWithStock,
    getSizesArrayForProduct,
    toggleSize,
    removeSize,
    onNoteChange,
    uploadNoteFile,
    removeNoteFile,
    fileUploadLoading,
    getSizeGroup,
    isFormValid,
  } = useStoreConfirmSizes({
    designGroups,
    sizesForm,
    isReadOnly,
    isEditStorePage,
    sizeOrderCounts,
    storeId,
  });

  useEffect(() => {
    onValidityChange?.(isFormValid);
  }, [isFormValid, onValidityChange]);

  if (!designGroups.length) {
    return null;
  }

  return (
    <div className="store-confirm-sizes">
      <div className="store-confirm-sizes__design-group">
        {designGroups.map((designGroup) => {
          const products = productsWithStock[designGroup.name] ?? [];

          return (
            <div key={designGroup.name} className="store-confirm-sizes__design-group-section">
              <div className="store-confirm-sizes__header">
                <div className="store-confirm-sizes__header-top">
                  <span className="store-confirm-sizes__header-title">{designGroup.name}</span>
                </div>
                <p className="store-confirm-sizes__header-subtitle">
                  {STORE_CONFIRM_SIZES_DESIGN_HEADER_SUBTITLE}
                </p>
              </div>

              <div className="store-confirm-sizes__product-lists">
                {products.map((product, index) => {
                  const sizeGroup = getSizeGroup(product.orderItemId);
                  const stockProps = product.stocksData?.stockProps;
                  const showNotesArea =
                    isSalesUser &&
                    stockProps &&
                    !product.stocksData?.isFpExclusiveAndLowOrOutOfStock;
                  const noteValue = sizeGroup?.stockNotes?.note ?? '';
                  const fileValue = sizeGroup?.stockNotes?.file ?? '';
                  const isUploading = fileUploadLoading[product.orderItemId];

                  return (
                    <React.Fragment key={`${product.orderItemId}-${index}`}>
                      <ProductSizeSelection
                        product={product}
                        sizes={getSizesArrayForProduct(product.orderItemId)}
                        stockData={sizeGroup?.stocksData ?? product.stocksData}
                        isReadOnly={isReadOnly}
                        onToggleSize={(sizeLabel) => toggleSize(product, sizeLabel)}
                        onRemoveSize={(sizeLabel) => removeSize(product, sizeLabel)}
                      />

                      {isSalesUser && stockProps && (
                        <div className="stock-message-container">
                          <StockWarningMessage stockProps={stockProps} />
                        </div>
                      )}

                      {showNotesArea && (
                        <div className="notes-area-container">
                          <div
                            className={`notes-area-container-inner ${isReadOnly ? 'disabled' : ''}`}
                          >
                            <Textarea
                              name={`stock-note-${product.orderItemId}`}
                              placeholder={STORE_CONFIRM_SIZES_NOTE.PLACEHOLDER}
                              value={noteValue}
                              disabled={isReadOnly}
                              rows={2}
                              onChange={(event) =>
                                onNoteChange(product.orderItemId, event.target.value)
                              }
                            />

                            <input
                              type="file"
                              hidden
                              id={`upload-btn-${product.orderItemId}`}
                              ref={(el) => {
                                fileInputRefs.current[product.orderItemId] = el;
                              }}
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) {
                                  uploadNoteFile(product.orderItemId, file).catch(() => undefined);
                                }
                              }}
                            />

                            <button
                              type="button"
                              className={`upload-btns ${isReadOnly ? 'disabled' : ''}`}
                              aria-label="Upload attachment"
                              onClick={() => {
                                if (!isReadOnly) {
                                  fileInputRefs.current[product.orderItemId]?.click();
                                }
                              }}
                            >
                              <Icon
                                source="/src/assets/svg/upload.svg"
                                width="24px"
                                height="24px"
                                className="default-btn"
                              />
                            </button>
                          </div>

                          {!noteValue.trim() && (
                            <div className="required-field-msg">
                              {STORE_CONFIRM_SIZES_NOTE.REQUIRED_MSG}
                            </div>
                          )}

                          {isUploading && (
                            <div className="file-skeleton-notes">
                              <div className="skeleton-text shimmer" />
                            </div>
                          )}

                          {fileValue && !isUploading && (
                            <div className="comment-files">
                              <div className={`attachment-pill ${isReadOnly ? 'disabled' : ''}`}>
                                <a
                                  className={`uploaded-file-name ${isReadOnly ? 'disabled' : ''}`}
                                  href={`${V3_APP_BASE_URL}api/v4/s3/download?file=${encodeURIComponent(
                                    fileValue
                                  )}`}
                                  download
                                >
                                  <span title={fileValue}>
                                    {fileValue.length > 28
                                      ? `${fileValue.slice(0, 28)}...`
                                      : fileValue}
                                  </span>
                                </a>
                                {!isReadOnly && (
                                  <button
                                    type="button"
                                    className="remove-file-btn"
                                    aria-label="Remove file"
                                    onClick={() => {
                                      removeNoteFile(product.orderItemId);
                                      const input = fileInputRefs.current[product.orderItemId];
                                      if (input) input.value = '';
                                    }}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {index < products.length - 1 && (
                        <div className="store-confirm-sizes__item-separator" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StoreConfirmSizes;
